import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import { Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { colors, isHiddenClass, stripHiddenClasses, isLineRegion, PERSON_REGIONS } from '../utils/colors';
import "./vehicle.css";  

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler);
const MAX_RENDER_POINTS = 400;
// zoom 1-2   -> hour-wise   (unit = 3600s)
// zoom 3-10  -> minute-wise (unit = 60s)
// zoom 11-20 -> second-wise (unit = 1s)
const getGranularityUnit = (zoom: number) => {
  if (zoom <= 2) return 3600;
  if (zoom <= 10) return 60;
  return 1;
};

const   VehicleDashboard = React.memo(({ activeTabData, apiData, selectedZone, setSelectedZone, loading, onTimeSelect, currentTime,
  handleFullDayCount, chartType, setChartType, zoneData, pedSeries, range }: any) => {
  const chartRef = useRef<any>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef: any = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const TOOLTIP_SIDE = "right" as "left" | "right";
  const { globalZoom: playerZoom } = useSelector((state: RootState) => state.service);
  // A multi-day range has no player, so no zoom: one point per hour, the
  // x-axis runs from the first day's midnight to the last day's 23:00.
  const globalZoom = range ? 1 : playerZoom;
  const rangeStart = useMemo(
    () => (range ? dayjs(range.start).startOf("day").toDate() : null),
    [range?.start]
  );
  const rangeHours = range ? (dayjs(range.end).diff(dayjs(range.start), "day") + 1) * 24 : 0;
  const { activeCarDetails, selectedDate } = useSelector((state: RootState) => state.service);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [filterLoading, setFilterLoading] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const manualOverrideRef = useRef(false);
  const isAutoScrollRef = useRef(false);
  const skipAutoScrollRef = useRef(false);
  const [debouncedZoom, setDebouncedZoom] = useState(globalZoom);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedZoom(globalZoom), 150);
    return () => clearTimeout(t);
  }, [globalZoom]);

  const granularityUnit = useMemo(
    () => (range ? 3600 : getGranularityUnit(debouncedZoom)),
    [debouncedZoom, range]
  );
  useEffect(() => {
    if (!selectedClass) return;
    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedClass]);

  const toggleClass = (cls: string) => {
    setFilterLoading(true);
    setSelectedClass((prev) => (prev === cls ? null : cls));
  };

  const lastUnitIndexRef = useRef<number>(-1);
  useEffect(() => {
    lastUnitIndexRef.current = -1;
  }, [granularityUnit]);

  useEffect(() => {
    if (currentTime == null) return;
    const unitIndex = Math.floor(currentTime / granularityUnit);
    if (lastUnitIndexRef.current === unitIndex) return;
    lastUnitIndexRef.current = unitIndex;
    setSelectedIndex(unitIndex);
  }, [currentTime, granularityUnit]);

  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    containerRef.current = node;
    if (node) {
      setContainerWidth(node.getBoundingClientRect().width);
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      ro.observe(node);
      resizeObserverRef.current = ro;
    }
  }, []);

  // Person appears in the stat chips for "All" and for person-counting lines
  // (ped_cross), but not for vehicle lines. The chart never plots it (see
  // `plottable`), and RightPanel strips it from vehicle-line bars.
  const visibleClasses = useMemo(() => {
    const all = apiData?.classes || [];
    if (!selectedZone) return all;
    if (PERSON_REGIONS.has(String(selectedZone).toLowerCase())) return all;
    return all.filter((cls: string) => !isHiddenClass(cls));
  }, [apiData?.classes, selectedZone]);

  // On "All" the person series comes from the pedestrian crossing so it agrees
  // with the chip. A selected region already returns its own scoped data.
  const pedPersonByTime = useMemo(() => {
    if (selectedZone || !pedSeries?.slots?.length) return null;
    const m = new Map<string, number>();
    pedSeries.slots.forEach((it: any) => m.set(it.time, Number(it.person || 0)));
    return m;
  }, [pedSeries, selectedZone]);

  const fullDayData = useMemo(() => {
    if (!apiData?.slots?.length || !visibleClasses.length) return [];
    const start = rangeStart ? new Date(rangeStart) : new Date();
    if (!rangeStart) start.setHours(0, 0, 0, 0);
    const bucketMap = new Map<number, any>();
    apiData.slots.forEach((item: any) => {
      const date = new Date(item.time.replace(" ", "T"));
      // A range counts hours from its first day, read from the slot's own
      // "YYYY-MM-DD HH" so no browser time-zone shift moves a slot; a day
      // counts seconds of the day.
      const secOfDay = rangeStart
        ? (dayjs(item.time.slice(0, 10)).diff(dayjs(range.start), "day") * 24 + Number(item.time.slice(11, 13))) * 3600
        : date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
      const index = Math.floor(secOfDay / granularityUnit);
      if (index < 0 || (rangeStart && index >= rangeHours)) return;
      let row = bucketMap.get(index);
      if (!row) {
        row = {
          time: new Date(start.getTime() + index * granularityUnit * 1000),
          realTime: null,
        };
        visibleClasses.forEach((cls: string) => {
          row[cls] = 0;
        });
        bucketMap.set(index, row);
      }
      visibleClasses.forEach((cls: any) => {
        const raw = pedPersonByTime && isHiddenClass(cls)
          ? pedPersonByTime.get(item.time) || 0
          : item[cls] || 0;
        row[cls] += Number(raw);
      });
      row.avgSpeed = item.avgSpeed || 0;
      row.realTime = date;
    });
    if (bucketMap.size === 0) return [];
    // A range always shows every hour of every day, empty ones as zero.
    const maxIndex = rangeStart ? rangeHours - 1 : Math.max(...Array.from(bucketMap.keys()));
    const result: any[] = new Array(maxIndex + 1);
    for (let i = 0; i <= maxIndex; i++) {
      if (bucketMap.has(i)) {
        result[i] = bucketMap.get(i);
      } else {
        const row: any = {
          time: new Date(start.getTime() + i * granularityUnit * 1000),
          realTime: null,
        };
        visibleClasses.forEach((cls: string) => {
          row[cls] = 0;
        });
        result[i] = row;
      }
    }
    return result;
  }, [apiData, granularityUnit, visibleClasses, pedPersonByTime, rangeStart, rangeHours]);

  const bucketSize = useMemo(() => {
    const totalMinutes = fullDayData.length;
    if (!totalMinutes) return 1;
    // 20 days are 480 hours; keep every hour rather than merging pairs.
    const targetPoints = range ? 24 * 20 : MAX_RENDER_POINTS * Math.max(1, globalZoom);
    return totalMinutes > targetPoints ? Math.ceil(totalMinutes / targetPoints) : 1;
  }, [fullDayData.length, globalZoom, range]);

  const visibleData = useMemo(() => {
    if (bucketSize <= 1) return fullDayData;
    const bucketed: any[] = [];
    for (let i = 0; i < fullDayData.length; i += bucketSize) {
      const chunk = fullDayData.slice(i, i + bucketSize);
      const row: any = { time: chunk[0].time, realTime: chunk[0].realTime, __bucketSize: chunk.length };
      visibleClasses.forEach((cls: string) => {
        row[cls] = chunk.reduce((sum: number, c: any) => sum + (c[cls] || 0), 0);
      });
      row.avgSpeed = chunk.find((c: any) => c.avgSpeed)?.avgSpeed || 0;
      bucketed.push(row);
    }
    return bucketed;
  }, [fullDayData, bucketSize, visibleClasses]);

  const chartWidthPx = useMemo(() => {
    if (!containerWidth) return null;
    return globalZoom > 1 ? Math.round(containerWidth * globalZoom) : containerWidth;
  }, [containerWidth, globalZoom]);

  const timeFormatOptions = useMemo((): Intl.DateTimeFormatOptions => {
    if (granularityUnit === 3600) return { hour: "numeric", hour12: true };
    if (granularityUnit === 60) return { hour: "numeric", minute: "2-digit", hour12: true };
    return { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true };
  }, [granularityUnit]);

  const labels = useMemo(
    () =>
      visibleData.map((item: any) =>
        range
          ? dayjs(item.time).format("D MMM, h A")
          : item.time.toLocaleTimeString([], timeFormatOptions)
      ),
    [visibleData, timeFormatOptions, range]
  );

  const selectedVisibleIndex = useMemo(() => {
    if (selectedIndex == null) return null;
    return Math.floor(selectedIndex / bucketSize);
  }, [selectedIndex, bucketSize]);

  const datasets = useMemo(() => {
    if (!visibleClasses.length) return [];
    // The chart plots whatever the chips show: person on "All" and on
    // person-counting lines, hidden on vehicle lines. Note the series come from
    // the global per-second slots, so a person curve is frame-wide detections,
    // not the selected line's crossings — the API has no per-region series.
    const filteredClasses = selectedClass
      ? visibleClasses.filter((cls: string) => cls === selectedClass)
      : visibleClasses;

    return filteredClasses.map((cls: string) => {
      const borderWidth = selectedVisibleIndex !== null ? 3 : 2;
      return {
        label: cls,
        data: visibleData.map((item: any) => item[cls] || 0),
        borderColor: selectedIndex !== null ? colors[cls] || "#34D399" : `${colors[cls]}99`,
        backgroundColor: selectedIndex !== null ? `${colors[cls]}25` : `${colors[cls]}12`,
        fill: true,
        tension: 0.35,
        borderWidth,
        pointRadius: (ctx: any) => (ctx.dataIndex === selectedVisibleIndex ? 7 : 0),
        pointBackgroundColor: (ctx: any) =>
          ctx.dataIndex === selectedVisibleIndex ? "#ff0000" : "transparent",
        pointBorderColor: (ctx: any) =>
          ctx.dataIndex === selectedVisibleIndex ? "#ffffff" : colors[cls],
        pointBorderWidth: (ctx: any) => (ctx.dataIndex === selectedVisibleIndex ? 3 : 1),
        pointHoverRadius: 6,
        pointHoverBackgroundColor: "#ffffff",
        pointHoverBorderColor: colors[cls],
        pointHoverBorderWidth: 2,
      };
    });
  }, [visibleData, selectedIndex, selectedVisibleIndex, selectedClass, visibleClasses]);

  const externalTooltipHandler = (context: any) => {
    const { tooltip } = context;
    const el = tooltipRef.current;
    if (!el) return;

    if (!tooltip || tooltip.opacity === 0) {
      el.style.opacity = "0";
      return;
    }

    const rows = (tooltip.dataPoints || [])
      .map((dp: any) => {
        const color = dp.dataset?.borderColor || "#111827";
        return `<div style="display:flex;align-items:center;gap:6px;margin:2px 0;">
            <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;"></span>
            <span style="text-transform:capitalize;">${dp.dataset.label}</span>
            <span style="margin-left:auto;font-weight:600;">${dp.formattedValue}</span>
          </div>`;
      })
      .join("");

    const extra = (() => {
      if (chartType !== "ttc" && chartType !== "pet") return "";
      const idx = tooltip.dataPoints?.[0]?.dataIndex;
      if (idx == null) return "";
      const row = visibleData[idx];
      return row?.avgSpeed ? `Avg Speed: ${row.avgSpeed} km/h` : "";
    })();

    el.innerHTML = `
        <div style="font-weight:600;margin-bottom:0px;color:#111827;">${tooltip.title?.[0] || ""}</div>
        ${rows}
        ${extra ? `<div style="margin-top:6px;font-size:11px;color:#6b7280;">${extra}</div>` : ""}
      `;

    const canvas = context.chart.canvas as HTMLCanvasElement;
    const container = containerRef.current;
    if (!container) return;
    const canvasRect = canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const pointX = canvasRect.left - containerRect.left + tooltip.caretX;
    const pointY = canvasRect.top - containerRect.top + tooltip.caretY;
    const OFFSET = 14;
    const panelWidth = el.offsetWidth || 170;
    const panelHeight = el.offsetHeight || 100;
    let left = pointX + OFFSET;
    const wouldOverflowRight = left + panelWidth > containerRect.width;
    if (TOOLTIP_SIDE === "left" || wouldOverflowRight) {
      left = pointX - panelWidth - OFFSET;
    }
    left = Math.max(4, Math.min(left, containerRect.width - panelWidth - 4));
    let top = pointY - panelHeight / 2;
    top = Math.max(4, Math.min(top, containerRect.height - panelHeight - 4));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.opacity = "1";
  };

  const options: any = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    hover: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        mode: "index",
        intersect: false,
        external: externalTooltipHandler,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        ticks: { color: "#6b7280" },
      },
      x: {
        grid: { display: false },
        ticks: range
          ? {
              // One label per day, at its midnight.
              autoSkip: false,
              maxRotation: 0,
              callback: function (value: any, index: any) {
                const item = visibleData[index];
                if (!item || item.time.getHours() !== 0) return "";
                return dayjs(item.time).format("D MMM");
              },
            }
          : {
              maxTicksLimit: globalZoom === 1 ? 12 : Math.min(96, Math.round(24 * globalZoom)),
              callback: function (value: any, index: any) {
                const item = visibleData[index];
                if (!item) return "";
                return item.time.toLocaleTimeString([], timeFormatOptions);
              },
            },
      },
    },
    animation: { duration: 0 },
    onClick: (event: any, _elements: any[], chart: any) => {
      const points = chart.getElementsAtEventForMode(
        event,
        "nearest",
        { intersect: false },
        true
      );
      if (!points.length) return;
      const visibleIndex = points[0].index;
      const realIndex = visibleIndex * bucketSize;
      skipAutoScrollRef.current = true;
      setSelectedIndex(realIndex);
      onTimeSelect?.(realIndex * granularityUnit);
    },
  }), [globalZoom, visibleData, bucketSize, onTimeSelect, chartType, externalTooltipHandler, timeFormatOptions, granularityUnit, range]);

  const total = useMemo(() => {
    if (!visibleClasses.length) return 0;
    return fullDayData.reduce((sum: number, item: any) => {
      const rowSum = visibleClasses.reduce(
        (acc: number, cls: string) => acc + (item[cls] || 0),
        0
      );
      return sum + rowSum;
    }, 0);
  }, [fullDayData, visibleClasses]);

  // Per-class counts scoped to the selected line/zone (falls back to the
  // full-day/all-lines totals above when no zone is selected or no
  // zone-scoped data exists for it).
  const zoneStats: Record<string, number> | null = useMemo(() => {
    if (!selectedZone) return null;
    return stripHiddenClasses(zoneData?.class_counts_by_region?.[selectedZone], selectedZone) || null;
  }, [selectedZone, zoneData]);

  const displayTotal = useMemo(() => {
    if (!zoneStats) return total;
    return Object.values(zoneStats).reduce((sum: number, v: any) => sum + Number(v || 0), 0);
  }, [zoneStats, total]);

  const getClassCount = useCallback(
    (cls: string) => {
      if (zoneStats) return Number(zoneStats[cls] || 0);
      // On "All", person means pedestrian crossings (ped_cross), not the
      // frame-wide detections the global slots carry.
      if (isHiddenClass(cls)) {
        const regions = zoneData?.class_counts_by_region || {};
        return Object.entries(regions).reduce(
          (acc: number, [region, counts]: any) =>
            PERSON_REGIONS.has(String(region).toLowerCase())
              ? acc + Number(counts?.[cls] || 0)
              : acc,
          0
        );
      }
      return fullDayData.reduce((acc: number, item: any) => acc + (item[cls] || 0), 0);
    },
    [zoneStats, fullDayData, zoneData]
  );

  useEffect(() => {
    handleFullDayCount?.(total);
  }, [total, handleFullDayCount]);

  useEffect(() => {
    setSelectedIndex(null);
    manualOverrideRef.current = false;
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [activeCarDetails, selectedDate, range?.start, range?.end]);

  useEffect(() => {
    if (!filterLoading) return;
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [datasets]);

  const prevZoomRef = useRef(globalZoom);
  useEffect(() => {
    if (prevZoomRef.current <= 1 && globalZoom > 1) {
      manualOverrideRef.current = false;
    }
    prevZoomRef.current = globalZoom;
  }, [globalZoom]);

  useEffect(() => {
    if (globalZoom <= 1) return;
    if (currentTime == null) return;

    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      return;
    }

    const el = scrollRef.current;
    if (!el || !chartWidthPx) return;

    const totalUnits = fullDayData.length || 1;
    const pxPerUnit = chartWidthPx / totalUnits;
    const playheadX = (currentTime / granularityUnit) * pxPerUnit;

    if (manualOverrideRef.current) {
      const viewStart = el.scrollLeft;
      const viewEnd = viewStart + el.clientWidth;
      const stillVisible = playheadX >= viewStart && playheadX <= viewEnd;
      if (stillVisible) return;
      manualOverrideRef.current = false;
    }

    const maxScroll = Math.max(0, chartWidthPx - el.clientWidth);
    const target = Math.max(0, Math.min(maxScroll, playheadX - el.clientWidth / 2));

    isAutoScrollRef.current = true;
    el.scrollLeft = target;
    requestAnimationFrame(() => {
      isAutoScrollRef.current = false;
    });
  }, [currentTime, globalZoom, chartWidthPx, fullDayData.length, granularityUnit]);

  const handleScroll = () => {
    if (isAutoScrollRef.current) return;
    manualOverrideRef.current = true;
  };

  const chartData = useMemo(
    () => ({
      labels,
      datasets,
    }),
    [labels, datasets]
  );

  const lineZoneKeys = useMemo(() => {
    const regions = zoneData?.class_counts_by_region || {};
    const allKeys = Object.keys(regions);
    return allKeys.filter((key) => {
      if (activeTabData === "lines") {
        return isLineRegion(key);
      }
      return !isLineRegion(key) && key.toLowerCase().includes("zone");
    });
  }, [zoneData?.class_counts_by_region, activeTabData]);

  //this useEffect auto select first index line & zone data
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (!hasAutoSelectedRef.current && lineZoneKeys.length > 0) {
      if (!selectedZone) {
        setSelectedZone?.(lineZoneKeys[0]);
      }
      hasAutoSelectedRef.current = true;
    }
  }, [lineZoneKeys, selectedZone, setSelectedZone]);

  return (
    <>
      {loading ? (
        <center style={{ marginTop: "50px" }}>
          <Spinner color="primary">Loading...</Spinner>
        </center>
      ) : (
        <>
          {fullDayData.length >= 0 ? (
            <div>
              <div className="top-bar">
                <div className="title-box">
                  <h3>Vehicle Classification</h3>
                  {/* <p>{selectedZone?.toUpperCase()}</p> */}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <select
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    style={{
                      fontSize: "12px",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      color: "#1e3a53",
                      fontWeight: 500,
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <option value="default">AI Data</option>
                    {!range && <option value="ttc">TTC</option>}
                    {!range && <option value="pet">PET</option>}
                  </select>
                </div>

                <div className="title-box">
                  {activeTabData == "lines" ? <h3>Line List</h3> : <h3>Zone List</h3>}
                  {/* <p>{selectedZone?.toUpperCase()}</p> */}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <select
                    value={selectedZone || ""}
                    onChange={(e) => setSelectedZone?.(e.target.value)}
                    style={{
                      fontSize: "12px",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      color: "#1e3a53",
                      fontWeight: 500,
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <option value="">All</option>
                    {lineZoneKeys.map((key) => (
                      <option key={key} value={key}>
                        {key.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="stats-scroll">
                  <div className="stats">
                    <div className="stat">
                      <span>All</span>
                      <h2>{Number(displayTotal).toLocaleString("en-IN")}</h2>
                    </div>
                    {visibleClasses.map((cls: string) => {
                      const sum = getClassCount(cls);
                      return (
                        <div className={`stat ${cls}`}
                          key={cls}
                          onClick={() => toggleClass(cls)}
                          style={{
                            cursor: "pointer",
                            opacity:
                              selectedClass === null || selectedClass === cls
                                ? 1
                                : 0.4,
                            border:
                              selectedClass === cls
                                ? "2px solid #2563eb"
                                : "2px solid transparent",
                            borderRadius: "0px",
                          }}>
                          <span style={{ textTransform: "capitalize" }}>{cls}</span>
                          <h3>{Number(sum).toLocaleString("en-IN")}</h3>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div ref={setContainerRef} style={{ position: "relative" }}>
                <div
                  ref={scrollRef}
                  onScroll={handleScroll}
                  style={{
                    width: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    border: globalZoom > 1 ? "1px solid #ccc" : "2px solid transparent",
                    borderRadius: "12px",
                    background: "#fff",
                  }}
                >
                  <div
                    className="chart-canvas-wrap"
                    style={{
                      width: chartWidthPx ? `${chartWidthPx}px` : "100%",
                      position: "relative",
                    }}
                  >
                    {filterLoading && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background: "rgba(255,255,255,0.7)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1000,
                        }}
                      >
                        <Spinner color="primary" />
                      </div>
                    )}
                    <Line
                      ref={chartRef}
                      data={chartData}
                      options={options}
                    />
                  </div>
                </div>
                <div
                  ref={tooltipRef}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    marginTop: "-55px",
                    width: "170px",
                    maxHeight: "220px",
                    overflowY: "auto",
                    padding: "5px 5px",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    background: "rgba(255,255,255,0.98)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    fontSize: "10px",
                    color: "#374151",
                    opacity: 0,
                    pointerEvents: "none",
                    transition: "opacity 0.1s ease",
                    zIndex: 10,
                  } as React.CSSProperties}
                />
              </div>
              <p className="date">
                {range
                  ? `${dayjs(range.start).format("D MMM")} – ${dayjs(range.end).format("D MMM YYYY")} · hourly`
                  : dayjs(selectedDate || undefined).toDate().toLocaleDateString()}
              </p>
            </div>
          ) : (
            <p style={{ textAlign: "center", fontSize: "12px", color: "#333" }}>No Graph Data Found</p>
          )}
        </>
      )}
    </>
  );
}
);

export default VehicleDashboard;